import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

export const EASE = [0.16, 1, 0.3, 1];

export function useReduceMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => setReduce(window.matchMedia("(prefers-reduced-motion: reduce)").matches), []);
  return reduce;
}

// Detección de «entró en pantalla» a prueba de fallos: IntersectionObserver
// y, de respaldo, un chequeo con getBoundingClientRect cada 0,6 s. Si el
// observador no avisa (p. ej. al saltar desde el menú), el chequeo activa el
// elemento en 1,2 s como máximo. Así ningún texto o foto puede quedar oculto:
// la animación solo mejora lo que ya es visible.
export function useInViewSafe(amount = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let done = false;
    let io = null;
    let iv = null;
    const activar = () => {
      if (done) return;
      done = true;
      setInView(true);
      if (io) io.disconnect();
      if (iv) clearInterval(iv);
    };
    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver(
        (entradas) => {
          for (const e of entradas) if (e.isIntersecting) activar();
        },
        { threshold: amount }
      );
      io.observe(el);
    }
    iv = setInterval(() => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.94 && r.bottom > 0) activar();
    }, 600);
    return () => {
      if (io) io.disconnect();
      clearInterval(iv);
    };
  }, [amount]);
  return [ref, inView];
}

export function Reveal({ children, delay = 0, y = 30, className = "", style, ...rest }) {
  const reduce = useReduceMotion();
  const [ref, inView] = useInViewSafe(0.15);
  if (reduce) {
    return (
      <div ref={ref} className={className} style={style} {...rest}>
        {children}
      </div>
    );
  }
  return (
    <motion.div
      ref={ref}
      className={className}
      style={style}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 0.75, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export function SectionTag({ children, className = "" }) {
  return (
    <div
      className={`flex items-center gap-3 font-mono text-[11px] sm:text-xs uppercase tracking-[0.25em] text-certero ${className}`}
    >
      <span className="h-px w-8 bg-certero/70" />
      <span>{children}</span>
    </div>
  );
}

// Título de sección que aparece LÍNEA POR LÍNEA al llegar a él (0,6 s por línea,
// pequeño desfase; solo la primera vez). Red de seguridad: si a los 1,2 s de
// estar en pantalla no arrancó, se muestra directo. Con «reducir movimiento»,
// aparece directo siempre.
export function TituloLineas({ lineas, className = "", as: Tag = "h2", testid }) {
  const reduce = useReduceMotion();
  const [ref, inView] = useInViewSafe(0.15);
  if (reduce) {
    return (
      <Tag className={className} data-testid={testid}>
        {lineas.map((l, i) => (
          <span key={i} className="block">{l}</span>
        ))}
      </Tag>
    );
  }
  return (
    <Tag ref={ref} className={className} data-testid={testid}>
      {lineas.map((l, i) => (
        <span key={i} className="block overflow-hidden pb-1">
          <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={inView ? { y: "0%" } : { y: "112%" }}
            transition={{ duration: 0.6, delay: inView ? i * 0.18 : 0, ease: EASE }}
          >
            {l}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

// Imagen con recorte transparente (p. ej. el dron visto de frente) que entra
// subiendo desde abajo y queda flotando suavemente (±6 px en un ciclo de 4 s).
// Con «reducir movimiento»: aparece directa y quieta.
export function Flotante({ children, className = "", delay = 0 }) {
  const reduce = useReduceMotion();
  const [ref, inView] = useInViewSafe(0.2);
  if (reduce) {
    return (
      <div ref={ref} className={className}>
        {children}
      </div>
    );
  }
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 28 }}
      transition={{ duration: 0.7, delay, ease: EASE }}
    >
      <div className="flotar-suave">{children}</div>
    </motion.div>
  );
}

// Cifra que cuenta de 0 a su valor en 1 s cuando aparece en pantalla (solo la
// primera vez). En rangos («30 a 40 ha») los dos números cuentan a la vez.
// El valor final es exactamente el texto original.
export function NumeroContador({ texto, className = "", testid }) {
  const [ref, inView] = useInViewSafe(0.6);
  const reduce = useReduceMotion();
  const partes = useRef(null);
  if (!partes.current) {
    const piezas = [];
    let ultimo = 0;
    for (const m of String(texto).matchAll(/\d[\d.,]*/g)) {
      if (m.index > ultimo) piezas.push({ t: String(texto).slice(ultimo, m.index) });
      piezas.push({ n: m[0] });
      ultimo = m.index + m[0].length;
    }
    if (ultimo < String(texto).length) piezas.push({ t: String(texto).slice(ultimo) });
    partes.current = piezas;
  }
  const nums = partes.current.filter((p) => p.n).map((p) => parseInt(p.n.replace(/[.,]/g, ""), 10));
  const fmt = (i, v) => {
    const orig = partes.current.filter((p) => p.n)[i].n;
    if (orig.includes(".") && orig.split(".").length === 2 && orig.split(".")[1].length === 3) {
      return v.toLocaleString("es-BO");
    }
    return String(v);
  };
  const [actuales, setActuales] = useState(nums.map(() => 0));

  useEffect(() => {
    if (!inView || reduce) return;
    const t0 = performance.now();
    const dur = 1000;
    let raf;
    const paso = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      setActuales(nums.map((n) => Math.round(n * e)));
      if (p < 1) raf = requestAnimationFrame(paso);
    };
    raf = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, reduce]);

  if (reduce) {
    return (
      <span ref={ref} className={className} data-testid={testid}>
        {texto}
      </span>
    );
  }

  const render = [];
  let ni = 0;
  for (const p of partes.current) {
    if (p.n) {
      render.push(<span key={`n${ni}`}>{fmt(ni, actuales[ni] ?? nums[ni])}</span>);
      ni += 1;
    } else {
      render.push(<span key={`t${i_of(p)}`}>{p.t}</span>);
    }
  }
  function i_of(p) {
    return partes.current.indexOf(p);
  }
  return (
    <span ref={ref} className={className} data-testid={testid}>
      {render}
    </span>
  );
}

// Foto grande con cortina suave de abajo hacia arriba + leve acercamiento
// (solo la primera vez). Tres tratamientos: normal (105 % → 100 %, 0,8 s),
// con parallax (la foto se mueve un poco más lento que la página) o con zoom
// documental lento (100 % → 108 % en 12 s, para portadas). Con «reducir
// movimiento»: foto directa, sin cortina.
export function FotoRevelada({ src, alt, className = "", testid, parallax = false, kenburns = false, loading = "lazy" }) {
  const reduce = useReduceMotion();
  const [ref, inView] = useInViewSafe(0.25);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  if (reduce) {
    return (
      <div ref={ref} className={`relative overflow-hidden ${className}`} data-testid={testid}>
        <img src={src} alt={alt} loading={loading} decoding="async" className="h-full w-full object-cover" />
      </div>
    );
  }

  if (kenburns) {
    return (
      <div ref={ref} className={`relative overflow-hidden ${className}`} data-testid={testid}>
        <img
          src={src}
          alt={alt}
          loading={loading}
          decoding="async"
          className={`h-full w-full object-cover ${inView ? "kenburns-lenta" : ""}`}
        />
        <motion.div
          className="absolute inset-0 bg-abyss"
          initial={{ y: 0 }}
          animate={inView ? { y: "-100%" } : { y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          aria-hidden="true"
        />
      </div>
    );
  }

  if (!parallax) {
    return (
      <div ref={ref} className={`relative overflow-hidden ${className}`} data-testid={testid}>
        <motion.img
          src={src}
          alt={alt}
          loading={loading}
          decoding="async"
          initial={{ scale: 1.05 }}
          animate={inView ? { scale: 1 } : { scale: 1.05 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="h-full w-full object-cover"
        />
        <motion.div
          className="absolute inset-0 bg-abyss"
          initial={{ y: 0 }}
          animate={inView ? { y: "-100%" } : { y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          aria-hidden="true"
        />
      </div>
    );
  }

  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`} data-testid={testid}>
      <motion.img
        src={src}
        alt={alt}
        loading={loading}
        decoding="async"
        style={{ y }}
        initial={{ scale: 1.12 }}
        animate={inView ? { scale: 1.08 } : { scale: 1.12 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="h-full w-full object-cover"
      />
      <motion.div
        className="absolute inset-0 bg-abyss"
        initial={{ y: 0 }}
        animate={inView ? { y: "-100%" } : { y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        aria-hidden="true"
      />
    </div>
  );
}
